import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BiDevPlayerEventsExtMasterFunnelSqlMap } from '../sql-maps/bidev-player-events-ext-master-funnel-agg-cleaned';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';

export interface FunnelsMasterFunnelPlayerEventsDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

/**
 * Calculates stats related to master funnel
 * Emits data of schema:
 * - `master_funnel_step_id`
 * - `cnt`
 * - `master_funnel_step_no`
 * - `screen_name`
 * - `percentage`
 * - `pct_diff`
 * - `error`
 */
export class FunnelsMasterFunnelPlayerEventsDataLoaderFactory extends Factory<
    DataLoader,
    FunnelsMasterFunnelPlayerEventsDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'FunnelsMasterFunnelPlayerEventsDataLoaderFactory';

    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(
        options: FunnelsMasterFunnelPlayerEventsDataLoaderFactoryOptions
    ) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log(
                    'FunnelsMasterFunnelPlayerEventsDataLoaderFactory',
                    queryContext
                );

                // ignore columns that are not supported in this query
                // const ignoredContextColumnIds = [
                //     'ACTIVITY_DATE',
                //     'ACTIVITY_APP_VERSION',
                // ];

                const bqTableSqlMap = BiDevPlayerEventsExtMasterFunnelSqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    bqTableSqlMap.sqlMap.columns
                );

                const dsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    dsColumns.missing
                );

                //filter out ignored columns from dsColumns.present
                dsColumns.present = dsColumns.present;
                // .filter(
                //     (col) => !ignoredContextColumnIds.includes(col.id)
                // );

                const limitStepsSetting =
                    queryContext.settings[
                        QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS
                    ];

                const isMasterFunnelStepIdFiltered =
                    queryContext.datasourceColumns.some(
                        (col) =>
                            col.id === 'FUNNELS_MASTER_FUNNEL_STEP_ID' &&
                            col.state.isFilterApplied === true
                    );

                const limitStepsSql = `LIMIT ${queryContext.settings['funnels.limitDetailSteps']}`;

                // const limitStepsSql = isMasterFunnelStepIdFiltered
                //     ? limitStepsSetting
                //         ? `LIMIT ${queryContext.settings['funnels.limitDetailSteps']}`
                //         : ''
                //     : 'LIMIT 0 -- No filter on master_funnel_step_id';

                const cntStrategy =
                    queryContext.settings['funnels.stepCountStrategy'] ??
                    'master_step_cnt';

                const eventNameGroupOtherSql = (() => {
                    const setting =
                        queryContext.settings['funnels.otherGroupingStrategy'];

                    let sql = '';
                    const event_name_sql = `add_totals.event_name`;

                    switch (setting) {
                        case 'raw':
                            sql = `${event_name_sql}`;
                            break;
                        case 'dynamic':
                            sql = `IF (dynamic_other.is_dynamic_other, 'Other' , ${event_name_sql})`;
                            break;
                        case 'master_step':
                            sql = `IF (is_master_funnel_step_scope_other, 'Other' , ${event_name_sql})`;
                            break;
                        case 'adventure':
                            sql = `IF (is_adventure_scope_other, 'Other' , ${event_name_sql})`;
                            break;
                        default:
                            throw new Error(
                                `Unknown otherGroupingStrategy: ${setting}`
                            );
                    }

                    return sql;
                })();

                const stepNameGroupOtherSql = (() => {
                    const setting =
                        queryContext.settings['funnels.otherGroupingStrategy'];

                    let sql = '';
                    const step_name_sql = `CONCAT(${[
                        // event_name
                        `'\\n | event_name: '`,
                        `add_totals.event_name`,
                        // step_id
                        `'\\n | step_id: '`,
                        `add_totals.step_name`,
                        // screen_name
                        `'\\n | screen: ['`,
                        `ARRAY_TO_STRING(screen_name, ', ')`,
                        `']'`,
                    ].join(', ')})`;

                    switch (setting) {
                        case 'raw':
                            sql = `${step_name_sql}`;
                            break;
                        case 'dynamic':
                            sql = `IF (dynamic_other.is_dynamic_other, 'Other' , ${step_name_sql})`;
                            break;
                        case 'master_step':
                            sql = `IF (is_master_funnel_step_scope_other, 'Other' , ${step_name_sql})`;
                            break;
                        case 'adventure':
                            sql = `IF (is_adventure_scope_other, 'Other' , ${step_name_sql})`;
                            break;
                        default:
                            throw new Error(
                                `Unknown otherGroupingStrategy: ${setting}`
                            );
                    }
                    return sql;
                })();

                const where_excl_details = (() => {
                    const filteredDsColumns = dsColumns.present.filter(
                        (col) => {
                            return (
                                col.id !== 'FUNNELS_MASTER_FUNNEL_STEP_ID' &&
                                col.id !== 'FUNNELS_DETAILS_EVENT_NAME' &&
                                col.id !== 'FUNNELS_DETAILS_SCREEN_NAME'
                            );
                        }
                    );
                    const { where } = bqTableSqlMap.compileSQLStatements(
                        filteredDsColumns,
                        queryContext.settings,
                        options
                    );
                    return where;
                })();

                const { where, join } = bqTableSqlMap.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options,
                    {
                        where: [],
                    }
                );

                const query = `
-- DataLoaderId: ${this.instanceId}

-- zapytanie uwzględnia county, total_county oraz grupowanie stepów do others
-- total cnt może być policzony w tabeli
WITH step_total_cnt AS
(
    SELECT 
      event_name
      , step_name
      , SUM(cnt) AS total_cnt
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        bqTableSqlMap.sqlMap.dataset,
        bqTableSqlMap.sqlMap.tableId
    )}\`
    ${where_excl_details}
    ${SqlComposer.GROUP_BY(['event_name', 'step_name'])}
)

, step_cnt AS
(
  SELECT
    event_name
    , step_name
    , is_master_funnel_step_scope_other
    , is_adventure_scope_other
    , ARRAY_AGG(DISTINCT screen_name) AS screen_name
    , SUM(cnt) AS cnt
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      bqTableSqlMap.sqlMap.dataset,
      bqTableSqlMap.sqlMap.tableId
  )}\`
  ${join}
  ${where}
  ${SqlComposer.GROUP_BY([
      'event_name',
      'step_name',
      'is_master_funnel_step_scope_other',
      'is_adventure_scope_other',
  ])}
)

-- dynamic_other może być przydatny gdy wybierzemy kilka stepów, ponieważ is_master_funnel_step_scope_other został przeliczany tylko w konteksice jednego stepu
, dynamic_other AS
(
  SELECT
    event_name
    , step_name
    , cnt / MAX ( cnt ) OVER () < 0.03 AS is_dynamic_other
  FROM step_cnt
)

, add_totals AS
(
  SELECT
    step_cnt.*
    , step_total_cnt.total_cnt
  FROM step_cnt
  LEFT JOIN step_total_cnt
    ON step_cnt.event_name = step_total_cnt.event_name
    AND step_cnt.step_name = step_total_cnt.step_name
)

, group_others AS
(
  SELECT
    ${eventNameGroupOtherSql} AS event_name
    , ${stepNameGroupOtherSql} AS step_name
    , SUM(cnt) AS master_step_cnt
    , SUM(total_cnt) AS total_cnt
  FROM add_totals
  LEFT JOIN dynamic_other
    ON add_totals.event_name = dynamic_other.event_name
    AND add_totals.step_name = dynamic_other.step_name
  ${SqlComposer.GROUP_BY(['event_name', 'step_name'])}
)


SELECT
  -- add row_number prefix to step_name
  CONCAT(CAST(ROW_NUMBER() OVER (ORDER BY ${cntStrategy} DESC) AS STRING), '. ', step_name) AS step_name
  , ${cntStrategy}
  , 1 - ${cntStrategy} / LAG(${cntStrategy}) OVER (ORDER BY ${cntStrategy} DESC) AS pct_diff
  -- percent difference between first and current step
  , 1 - ${cntStrategy} / FIRST_VALUE(${cntStrategy}) OVER (ORDER BY ${cntStrategy} DESC) AS pct_diff_first
FROM group_others
${SqlComposer.ORDER_BY([`${cntStrategy} DESC`])}
${limitStepsSql}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: dsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage: dsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
